import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createInitialPagination,
  DEFAULT_PER_PAGE,
} from "../../utils/pagination";

// modeごとに GET のURLを切り替え
const buildIndexUrl = ({ mode, pageTypes, page, per, sort }) => {
  const base =
    mode === "company" ? "/company/suggestions.json" : "/suggestions.json";
  const params = new URLSearchParams();

  if (pageTypes.length > 0) {
    pageTypes.forEach((pageType) => {
      params.append("page_types[]", pageType);
    });
  }

  if (sort) params.append("sort", sort);
  params.append("page", page); // ページ番号を追加
  params.append("per", per); // 1ページあたりの件数

  return `${base}?${params.toString()}`;
};

/**
 * pageTypes に応じて suggestions を取得する共通Hook
 * - mode: "company" | "user"
 * - pageTypes: 対象画面の配列
 * - page: 現在のページ番号
 * - per: 1ページあたりの件数
 * - sort: "priority" | "created_at"
 */
export default function useSuggestionsIndex({
  mode,
  pageTypes = [],
  page = 1,
  per = DEFAULT_PER_PAGE,
  sort = "priority",
}) {
  // 一覧データ
  const [suggestions, setSuggestions] = useState([]);

  // ページング情報を管理
  const [pagination, setPagination] = useState(createInitialPagination(per));

  // ローディング状態
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  // 一覧を取得する関数
  const refetch = useCallback(async () => {
    // companyは常に取得OK、userはpageTypeがある時だけ取得
    const shouldFetch = mode === "company" || !!pageTypes.length;

    if (!shouldFetch) {
      setSuggestions([]);
      setPagination(createInitialPagination(per));
      setIsSuggestionsLoading(false);
      return;
    }

    // company は pageType が空でも取得する（全件）
    setIsSuggestionsLoading(true);

    try {
      const res = await fetch(
        buildIndexUrl({ mode, pageTypes, page, per, sort }),
        {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        },
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();

      // 必ず新しい配列参照でセット（再描画保証）
      const next = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setSuggestions([...next]);

      // pagination もレスポンスから更新
      setPagination(data?.pagination ?? createInitialPagination(per));
    } catch (e) {
      console.error(e);

      // 取得失敗時は空にする
      setSuggestions([]);
      setPagination(createInitialPagination(per));

      toast.error("サジェストの取得に失敗しました。", {
        toastId: "suggest-fetch-error",
      });
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [mode, pageTypes, page, per, sort]);

  // 初期表示 & 条件変更時に取得
  useEffect(() => {
    refetch();
  }, [refetch]);

  // 外部に公開する値
  return useMemo(
    () => ({
      suggestions,
      pagination,
      isSuggestionsLoading,
      loading: isSuggestionsLoading,
      refetch,
    }),
    [suggestions, pagination, isSuggestionsLoading, refetch],
  );
}
